import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Mail, Calendar, FlaskConical, MapPin, ShieldCheck, Search, Pencil, X as XIcon, Save } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ShowTestDataToggle, useShowDemoToggle } from "@/components/ShowTestDataToggle";
import { ArchiveStatusFilter, useArchiveFilter } from "@/components/ArchiveStatusFilter";
import { ArchiveActionsMenu } from "@/components/ArchiveActionsMenu";
import { StatusBadge } from "@/components/StatusBadge";
import api, { formatApiError } from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";
import { recordVisit } from "@/lib/recentVisits";
import { toast } from "sonner";

export function AdminCustomersList() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showDemo, setShowDemo] = useShowDemoToggle("admin.customers.showDemo");
  const [archived, setArchived] = useArchiveFilter("admin.customers.archived");
  const [reloadKey, setReloadKey] = useState(0);
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => setMe(null));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (showDemo) params.set("show_demo", "true");
    if (archived !== "active") params.set("archived", archived);
    const qs = params.toString() ? `?${params.toString()}` : "";
    const t = setTimeout(() => {
      api.get(`/admin/customers${qs}`).then(({ data }) => setItems(data)).finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q, showDemo, archived, reloadKey]);

  return (
    <PortalShell title="Customers" subtitle="Admin · Customer records">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input data-testid="customers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or company" className="pl-10 h-11 rounded-full bg-white" />
        </div>
        <div className="flex items-center gap-3">
          <ArchiveStatusFilter value={archived} onChange={setArchived} testId="customers-archive-filter" />
          <ShowTestDataToggle showDemo={showDemo} onChange={setShowDemo} testId="customers-show-demo-toggle" />
        </div>
      </div>
      <div className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
        {loading ? <div className="p-10 text-center text-slate-500">Loading…</div> :
          items.length === 0 ? <div className="p-10 text-center text-slate-500">No customers match.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Customer</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Role</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Status</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Properties</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Bookings</th>
                  <th className="text-right p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(({ user, property_count, booking_count }) => {
                  const isGuest = user.has_account === false;
                  const rowKey = user.id || `guest-${user.email}`;
                  const openHref = isGuest ? `/admin/customers/${encodeURIComponent(user.email || "")}` : `/admin/customers/${user.id}`;
                  return (
                  <tr key={rowKey} data-testid={`customers-row-${user.id || user.email}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="p-4">
                      <div className="font-medium text-slate-900 inline-flex items-center gap-2">
                        {user.full_name || "—"}
                        {isGuest && (
                          <span data-testid="customers-row-guest-pill" className="px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800">Guest</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">{user.email}</div>
                      {user.phone && (
                        <div className="text-xs text-slate-400 mt-0.5" data-testid="customers-row-phone">{user.phone}</div>
                      )}
                    </td>
                    <td className="p-4 text-xs"><span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold capitalize">{user.role}</span></td>
                    <td className="p-4"><StatusBadge status={user.archived_at ? "archived" : (isGuest ? "guest" : "active")} /></td>
                    <td className="p-4 font-bold text-slate-900">{property_count}</td>
                    <td className="p-4 font-bold text-slate-900">{booking_count}</td>
                    <td className="p-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        <Link to={openHref} data-testid={`customers-open-${user.id || user.email}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-cyan-600 transition-colors">
                          <Pencil className="w-3 h-3" /> Open / Edit
                        </Link>
                        {!isGuest && (
                          <ArchiveActionsMenu
                            recordType={user.role === "member" ? "trade_partner" : "customer"}
                            recordId={user.id}
                            recordDisplay={user.full_name || user.email}
                            isArchived={!!user.archived_at}
                            onChanged={() => setReloadKey((k) => k + 1)}
                            hidden={me?.role && me.role !== "admin"}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PortalShell>
  );
}

export function AdminCustomerProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/customers/${id}`).then(({ data }) => {
      setData(data);
      // Record the visit in the command-palette "Recently viewed" cache so
      // the admin can jump back here from ⌘K without typing.
      const u = data?.user;
      if (u) {
        recordVisit({
          kind: "customer",
          id: u.id,
          label: u.full_name || u.email || "Customer",
          sublabel: u.email,
          to: `/admin/customers/${u.id}`,
        });
      }
    }).catch((e) => {
      // 404 with `code: "no_customer_profile"` is intentional — backend
      // returns the email + any bookings tied to it so we can render a
      // graceful "no profile" card instead of an infinite spinner.
      const detail = e?.response?.data?.detail;
      setError(detail || { message: e?.message || "Failed to load customer profile" });
    }).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <PortalShell title="Loading…" subtitle="Customer"><div className="text-slate-500">Loading…</div></PortalShell>;

  // Graceful "no profile" state — common when a booking was created with
  // an email that doesn't have a corresponding customer account yet.
  if (error && (typeof error === "object") && error.code === "no_customer_profile") {
    const fmt = (d) => formatDateAU(d);
    return (
      <PortalShell title="Customer profile not found" subtitle="Customer">
        <Link to="/admin/customers" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900 mb-5" data-testid="customer-profile-not-found-back">
          <ArrowLeft className="w-4 h-4" /> All customers
        </Link>
        <div data-testid="customer-profile-not-found" className="rounded-3xl bg-white border border-slate-200 p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <span className="text-amber-700 font-bold">?</span>
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl font-semibold text-slate-900">No customer account for <span className="font-mono text-base">{error.email}</span></h2>
              <p className="text-sm text-slate-600 mt-1.5">{error.message}</p>
            </div>
            {error.guest_editable && (
              <GuestEmailEditor
                fromEmail={error.email}
                bookingCount={(error.bookings || []).length}
              />
            )}
          </div>
          {error.bookings && error.bookings.length > 0 && (
            <div className="mt-6">
              <h3 className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-3">Bookings under this email ({error.bookings.length})</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Ref</th>
                      <th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Test</th>
                      <th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Date</th>
                      <th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Status</th>
                      <th className="text-right p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {error.bookings.map((b) => (
                      <tr key={b.reference} className="border-t border-slate-100">
                        <td className="p-3 font-mono text-xs font-bold">{b.reference}</td>
                        <td className="p-3 text-slate-700 text-xs">{b.test_name}</td>
                        <td className="p-3 text-xs text-slate-500">{fmt(b.created_at)}</td>
                        <td className="p-3 text-xs capitalize">{b.status}</td>
                        <td className="p-3 text-right font-semibold text-slate-900 text-xs">${(b.total || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </PortalShell>
    );
  }

  if (!data) return <PortalShell title="Customer not found" subtitle="Customer"><div className="text-slate-500">Customer not found.</div></PortalShell>;

  const { user, properties, bookings, tests, schedules, emails } = data;
  const fmt = (d) => formatDateAU(d);
  const upcomingRetests = (schedules || []).filter((s) => s.next_due_at).sort((a, b) => a.next_due_at.localeCompare(b.next_due_at));
  const recentReports = (tests || []).filter((t) => t.status === "published").slice(0, 5);

  return (
    <PortalShell title={user.full_name || user.email} subtitle="Customer profile">
      <Link to="/admin/customers" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900 mb-5">
        <ArrowLeft className="w-4 h-4" /> All customers
      </Link>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 space-y-5">
          <CustomerDetailsCard user={user} onSaved={(fresh) => setData((d) => ({ ...d, user: fresh }))} />

          <section className="rounded-3xl bg-white border border-slate-200 p-5">
            <h3 className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-3 inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Upcoming retests</h3>
            {upcomingRetests.length === 0 ? <div className="text-sm text-slate-400">No active schedules</div> : (
              <div className="space-y-2">
                {upcomingRetests.map((s) => {
                  const overdue = new Date(s.next_due_at) < new Date();
                  return (
                    <div key={s.property_id + s.source_id} className="text-sm">
                      <div className="font-medium text-slate-900">{fmt(s.next_due_at)}</div>
                      <div className="text-xs text-slate-500">Test: {s.test_id} · cadence {s.cadence_months}mo {overdue && <span className="ml-1 px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 text-[9px] font-bold uppercase">Overdue</span>}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="rounded-3xl bg-white border border-slate-200 p-5">
            <h3 className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-3 inline-flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> Recent published reports</h3>
            {recentReports.length === 0 ? <div className="text-sm text-slate-400">No reports yet</div> : (
              <div className="space-y-2">
                {recentReports.map((t) => (
                  <div key={t.id} className="text-sm">
                    <div className="font-medium text-slate-900">{t.test_name} <span className="font-bold ml-1">{t.health_score}</span> <span className="text-[10px] text-slate-500 uppercase ml-1">{t.band}</span></div>
                    <div className="text-xs text-slate-500">{fmt(t.collected_at)} · sample {t.sample_number || "—"}</div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="lg:col-span-8 space-y-5">
          <section className="rounded-3xl bg-white border border-slate-200 p-5">
            <h3 className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-3 inline-flex items-center gap-1.5"><MapPin className="w-3 h-3" /> Properties ({properties.length})</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {properties.map((p) => (
                <Link key={p.id} to={`/admin/properties?q=${encodeURIComponent(p.address || '')}`} className="rounded-2xl border border-slate-200 p-4 hover:border-cyan-300 transition-colors">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-600">{p.property_type}</div>
                  <div className="text-sm font-semibold text-slate-900 mt-1">{p.nickname || p.address}</div>
                  <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-1">
                    {(p.sources || []).map((s) => <span key={s.id} className="px-1.5 py-0.5 rounded bg-slate-100 capitalize">{s.type.replace(/_/g, " ")}</span>)}
                  </div>
                </Link>
              ))}
              {properties.length === 0 && <div className="text-sm text-slate-400 col-span-2">No properties</div>}
            </div>
          </section>

          <section className="rounded-3xl bg-white border border-slate-200 p-5">
            <h3 className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-3 inline-flex items-center gap-1.5"><FlaskConical className="w-3 h-3" /> Bookings ({bookings.length})</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr><th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Ref</th><th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Test</th><th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Date</th><th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Status</th><th className="text-right p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Total</th></tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.reference} className="border-t border-slate-100">
                      <td className="p-3 font-mono text-xs font-bold">{b.reference}</td>
                      <td className="p-3 text-slate-700 text-xs">{b.test_name}</td>
                      <td className="p-3 text-xs text-slate-500">{fmt(b.created_at)}</td>
                      <td className="p-3 text-xs capitalize">{b.status}</td>
                      <td className="p-3 text-right font-semibold text-slate-900 text-xs">${(b.total || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                  {bookings.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-slate-400 text-sm">No bookings</td></tr>}
                </tbody>
              </table>
            </div>
          </section>

          <section data-testid="customer-emails" className="rounded-3xl bg-white border border-slate-200 p-5">
            <h3 className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-3 inline-flex items-center gap-1.5"><Mail className="w-3 h-3" /> Communication history ({emails.length})</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr><th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Sent</th><th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Subject</th><th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Category</th><th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Send status</th><th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Delivery</th></tr>
                </thead>
                <tbody>
                  {emails.map((e) => (
                    <tr key={e.id} className="border-t border-slate-100">
                      <td className="p-3 text-xs text-slate-500 whitespace-nowrap">{formatDateAU(e.created_at)}</td>
                      <td className="p-3 text-xs text-slate-700 max-w-[300px] truncate" title={e.subject}>{e.subject}</td>
                      <td className="p-3 text-xs text-slate-500">{e.category}</td>
                      <td className="p-3 text-xs"><span className={`px-2 py-0.5 rounded-full font-semibold ${e.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : e.status === 'failed' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'}`}>{e.status}</span></td>
                      <td className="p-3 text-xs text-slate-500 capitalize">{e.delivery_state || "—"}</td>
                    </tr>
                  ))}
                  {emails.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-slate-400 text-sm">No emails sent yet</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </PortalShell>
  );
}

/**
 * Feb 2026 — Inline email-correction widget for GUEST customers
 * (bookings captured without a users row). Rewrites `customer.email`
 * across every booking that shares the old address so bounces caused
 * by typos ("gmial.com") can be fixed without asking the customer to
 * re-book.
 */
function GuestEmailEditor({ fromEmail, bookingCount }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toEmail, setToEmail] = useState(fromEmail || "");

  const submit = async () => {
    if (!toEmail || toEmail === fromEmail) {
      toast.error("Enter a different, valid email");
      return;
    }
    const ok = window.confirm(
      `Rewrite the guest email on ${bookingCount} booking${bookingCount === 1 ? "" : "s"} from\n\n  ${fromEmail}\n\nto\n\n  ${toEmail}\n\nProceed?`,
    );
    if (!ok) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/customers/guest/email`, {
        from_email: fromEmail,
        to_email:   toEmail,
      });
      toast.success(`Rewrote ${data?.cascaded?.bookings ?? 0} booking(s) to ${data?.to}`);
      window.location.assign(`/admin/customers/${encodeURIComponent(data?.to || toEmail)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to rewrite guest email");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <Button
        data-testid="guest-email-edit-btn"
        onClick={() => setEditing(true)}
        className="h-8 px-3.5 rounded-full text-xs font-semibold bg-cyan-600 hover:bg-cyan-700 text-white shadow-sm shrink-0"
      >
        <Pencil className="w-3.5 h-3.5 mr-1.5" /> Fix email
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-2 shrink-0" data-testid="guest-email-editor">
      <Input
        data-testid="guest-email-editor-input"
        value={toEmail}
        onChange={(e) => setToEmail(e.target.value)}
        placeholder="Corrected email"
        className="h-8 text-sm w-64"
        disabled={saving}
      />
      <Button
        data-testid="guest-email-editor-save"
        onClick={submit}
        disabled={saving}
        className="h-8 px-3 rounded-full text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
      >
        <Save className="w-3 h-3 mr-1" /> {saving ? "Saving…" : "Save"}
      </Button>
      <Button
        data-testid="guest-email-editor-cancel"
        onClick={() => { setEditing(false); setToEmail(fromEmail || ""); }}
        disabled={saving}
        variant="outline"
        className="h-8 px-2 rounded-full text-xs text-slate-600"
      >
        <XIcon className="w-3 h-3" />
      </Button>
    </div>
  );
}



/**
 * Feb 2026 — Admin-only inline editor for a customer's core contact
 * fields (email / full name / phone / company).
 *
 * Email changes are the highest-risk edit: they cascade through
 * `bookings.customer.email`, `properties.owner_email` and
 * `retest_schedule.owner_email` on the server so downstream lookups
 * keep matching. We surface a confirmation dialog before writing an
 * email change so the admin doesn't type-o away a real customer's
 * login.
 */
function CustomerDetailsCard({ user, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState(user.email || "");
  const [fullName, setFullName] = useState(user.full_name || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [company, setCompany] = useState(user.company_name || "");
  const fmt = (d) => formatDateAU(d);

  const startEditing = () => {
    setEmail(user.email || "");
    setFullName(user.full_name || "");
    setPhone(user.phone || "");
    setCompany(user.company_name || "");
    setEditing(true);
  };

  const cancel = () => setEditing(false);

  const save = async () => {
    const nextEmail = (email || "").trim().toLowerCase();
    const currentEmail = (user.email || "").toLowerCase();

    // High-friction confirm on email change — this is the login/auth key.
    if (nextEmail && nextEmail !== currentEmail) {
      const ok = window.confirm(
        `Change email from\n\n  ${user.email}\n\nto\n\n  ${nextEmail}\n\nThis will update the customer's login, and cascade through ` +
        `their bookings, properties and retest schedules. They will need to use the new email next time they sign in.\n\nContinue?`
      );
      if (!ok) return;
    }

    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/customers/${user.id}`, {
        email:        nextEmail || undefined,
        full_name:    fullName.trim() || null,
        phone:        phone.trim() || null,
        company_name: company.trim() || null,
      });
      if (data.changed) {
        const cascadeBits = data.cascaded ? Object.entries(data.cascaded).filter(([, n]) => n).map(([k, n]) => `${n} ${k}`).join(", ") : "";
        toast.success(cascadeBits ? `Saved. Cascaded ${cascadeBits}.` : "Saved");
      } else {
        toast.info("No changes to save");
      }
      onSaved?.(data.user);
      setEditing(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      data-testid="customer-details-card"
      className="rounded-3xl bg-white border border-slate-200 p-5"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Customer details</h3>
        {!editing && user.id && (
          <Button
            data-testid="customer-details-edit-btn"
            onClick={startEditing}
            className="h-8 px-3.5 rounded-full text-xs font-semibold bg-cyan-600 hover:bg-cyan-700 text-white shadow-sm"
          >
            <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit details
          </Button>
        )}
      </div>

      {!editing && (
        <div className="space-y-2 text-sm">
          <FieldReadOnly label="Email"   value={user.email} testid="customer-detail-email" />
          <FieldReadOnly label="Name"    value={user.full_name} testid="customer-detail-name" />
          <FieldReadOnly label="Phone"   value={user.phone} testid="customer-detail-phone" />
          <FieldReadOnly label="Company" value={user.company_name} testid="customer-detail-company" />
          <FieldReadOnly label="Role · Status" value={`${user.role} · ${user.status}`} capitalize />
          <FieldReadOnly label="Joined"  value={fmt(user.created_at)} />
        </div>
      )}

      {editing && (
        <div className="space-y-3 text-sm">
          <EditField label="Email"   value={email}    onChange={setEmail}    type="email" required testid="customer-edit-email" />
          <EditField label="Name"    value={fullName} onChange={setFullName} testid="customer-edit-name" />
          <EditField label="Phone"   value={phone}    onChange={setPhone}    testid="customer-edit-phone" />
          <EditField label="Company" value={company}  onChange={setCompany}  testid="customer-edit-company" />
          <div className="flex items-center gap-2 pt-2">
            <Button
              data-testid="customer-edit-save-btn"
              onClick={save}
              disabled={saving || !email.trim()}
              className="rounded-full font-semibold btn-aqua px-4 h-9"
            >
              <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
            </Button>
            <Button
              data-testid="customer-edit-cancel-btn"
              onClick={cancel}
              variant="outline"
              disabled={saving}
              className="rounded-full font-semibold px-4 h-9 border-slate-300"
            >
              <XIcon className="w-3.5 h-3.5 mr-1.5" /> Cancel
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 leading-snug pt-1">
            Changing the email will update the customer&apos;s login and cascade through their bookings, properties and retest schedules.
          </p>
        </div>
      )}
    </section>
  );
}

function FieldReadOnly({ label, value, testid, capitalize = false }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{label}</div>
      <div className={`text-slate-900 ${capitalize ? "capitalize" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function EditField({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">{label}{required && <span className="text-rose-500 ml-0.5">*</span>}</label>
      <Input
        data-testid={testid}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 rounded-lg"
      />
    </div>
  );
}

